import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { format, addDays, addMonths, addYears } from "date-fns";
import { Shell } from "@/components/layout/Shell";
import { useContracts } from "@/hooks/useContracts";
import { useCurrencies } from "@/hooks/useCurrencies";
import { ContractSkeleton } from "./components/ContractSkeleton";
import { ContractError } from "./components/ContractError";
import { ContractHeader } from "./components/ContractHeader";
import { ContractExpiryWarning } from "./components/ContractExpiryWarning";
import { AttachmentsList } from "./components/AttachmentsList";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Calendar, Tag, RefreshCw, Coins, AlertTriangle, Building2 } from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useUserPreferences } from "@/hooks/useUserPreferences";
import { currencyConverter } from "@/services/currencyConverter";
import { toast } from "@/hooks/use-toast";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { DirectionText, getDirectionIcon, getDirectionTextValue } from "@/components/contract-types/ContractDirectionIcons";
import { useFeatureAccess } from "@/hooks/useFeatureAccess";
import { FeatureGuard } from "@/components/common/FeatureGuard";
import { SendContractEmailDialog } from '@/components/contracts/email/SendContractEmailDialog';
import { Mail } from 'lucide-react';
import { ContractServicesCard } from "@/components/contracts/view/ContractServicesCard";

const formatPaymentFrequency = (frequency: { interval: number; unit: string }, t: any) => {
  if (frequency.interval === 0) return t('oneTime');
  
  const unitKey = frequency.interval === 1 
    ? `timeUnit.${frequency.unit.toLowerCase()}`
    : `timeUnit.${frequency.unit.toLowerCase()}s`;
  
  return t('everyInterval', { 
    interval: frequency.interval,
    unit: t(unitKey, frequency.unit.toLowerCase())
  });
};

const calculateRenewalDate = (signedDate: Date, type: any) => {
  if (!type.auto_renews) {
    return null;
  }
  
  const interval = type.renewal_interval || 1;
  const unit = type.renewal_unit || 'Year';
  
  if (unit === 'Day') {
    return addDays(signedDate, interval);
  } else if (unit === 'Month') {
    return addMonths(signedDate, interval);
  } else if (unit === 'Year') {
    return addYears(signedDate, interval);
  }
  
  return null;
};

export default function ContractViewPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { useContractQuery, useDeleteContractMutation } = useContracts();
  const { useCurrencyQuery } = useCurrencies();
  const { useUserPreferencesQuery } = useUserPreferences();
  const { canUseCurrencyConversion } = useFeatureAccess();
  
  const [convertedAmount, setConvertedAmount] = useState<number | null>(null);
  const [conversionRate, setConversionRate] = useState<number>(1);
  const [isConverting, setIsConverting] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  
  const deleteContractMutation = useDeleteContractMutation();

  useEffect(() => {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (id && !uuidRegex.test(id)) {
      console.error(`Invalid contract ID format: ${id}`);
      navigate("/contracts");
      return;
    }
  }, [id, navigate]);

  const { 
    data: contract, 
    isLoading, 
    isError 
  } = useContractQuery(id || "");

  const { 
    data: latestCurrencyData
  } = useCurrencyQuery(contract?.currencyId || '');
  
  const { 
    data: userPreferences 
  } = useUserPreferencesQuery();
  
  const { 
    data: defaultCurrencyData,
    isLoading: isDefaultCurrencyLoading
  } = useCurrencyQuery(userPreferences?.defaultCurrencyId || '');

  useEffect(() => {
    const convertCurrency = async () => {
      if (!contract) return;
      
      if (!canUseCurrencyConversion) {
        setConvertedAmount(null);
        return;
      }
      
      if (
        latestCurrencyData && 
        defaultCurrencyData && 
        latestCurrencyData.id !== defaultCurrencyData.id &&
        contract.currencyId && 
        userPreferences?.defaultCurrencyId
      ) {
        setIsConverting(true);
        try {
          const result = await currencyConverter.convert(
            contract.expectedCost,
            contract.currencyId,
            userPreferences.defaultCurrencyId
          );
          
          setConvertedAmount(result.convertedAmount);
          setConversionRate(result.rate);
        } catch (error) {
          console.error("Error converting currency:", error);
          setConvertedAmount(null);
        } finally {
          setIsConverting(false);
        }
      } else {
        setConvertedAmount(null);
      }
    };
    
    convertCurrency();
  }, [latestCurrencyData, defaultCurrencyData, contract?.expectedCost, contract?.currencyId, userPreferences?.defaultCurrencyId, contract, canUseCurrencyConversion]);

  const handleDeleteContract = async () => {
    if (!id) return;
    
    try {
      await deleteContractMutation.mutateAsync(id);
      navigate("/contracts");
    } catch (error) {
      console.error("Error deleting contract:", error);
    }
  };

  const getTimelineButtonText = () => {
    if (contract.type.direction === "incoming") {
      return t('viewIncomeTimeline');
    }
    return t('viewCostTimeline');
  };

  if (isLoading) {
    return (
      <Shell>
        <ContractSkeleton />
      </Shell>
    );
  }

  if (isError || !contract) {
    return (
      <Shell>
        <ContractError />
      </Shell>
    );
  }

  const daysUntilExpiry = Math.floor(
    (contract.expiryDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)
  );
  
  const renewalDate = calculateRenewalDate(contract.signedDate, contract.type);
  const hasRenewalInfo = Boolean(contract.type?.auto_renews && renewalDate);

  const getCurrencyDisplay = () => {
    if (latestCurrencyData) {
      return {
        symbol: latestCurrencyData.symbol,
        code: latestCurrencyData.code
      };
    } else if (contract.currency) {
      return {
        symbol: contract.currency.symbol,
        code: contract.currency.code
      };
    }
    return { symbol: '$', code: 'USD' };
  };

  const currencyDisplay = getCurrencyDisplay();
  
  const isIncomingContract = contract.type.direction === "incoming";
  const costLabel = isIncomingContract ? t('expectedIncome') : t('expectedCost');

  return (
    <Shell>
      <div className="space-y-6">
        <ContractHeader
          id={contract.id}
          supplierName={contract.supplierName}
          organisationnumber={contract.organisationnumber}
          importance={contract.importance}
          typeName={contract.type.name}
          categoryName={contract.category?.name}
          onDelete={handleDeleteContract}
          contractDirection={contract.type.direction}
        />

        <FeatureGuard feature="contract_pdf_email">
          <Button 
            onClick={() => setEmailDialogOpen(true)}
            className="w-full sm:w-auto"
          >
            <Mail className="h-4 w-4 mr-2" />
            {t('sendContract')}
          </Button>
        </FeatureGuard>

        <ContractExpiryWarning
          daysUntilExpiry={daysUntilExpiry}
          hasRenewalInfo={hasRenewalInfo}
        />

        <div className="grid gap-6 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>{t('contractDetails')}</CardTitle>
              <CardDescription>{t('basicInformation')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2 items-start">
                <Building2 className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium">{t('supplierDetails')}</p>
                  <p className="text-sm text-muted-foreground">
                    {contract.supplierName}
                    {contract.organisationnumber && (
                      <span className="ml-2 text-xs text-muted-foreground">
                        ({contract.organisationnumber})
                      </span>
                    )}
                  </p>
                </div>
              </div>
              
              <div className="flex gap-2 items-start">
                <Calendar className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium">{hasRenewalInfo ? t('renewalDate') : t('expiryDate')}</p>
                  <p className="text-sm text-muted-foreground">
                    {format(contract.expiryDate, "MMMM d, yyyy")}
                    {hasRenewalInfo && (
                      <span className="ml-2 inline-flex items-center text-xs text-primary">
                        <RefreshCw className="h-3 w-3 mr-1" />
                        {t('autoRenews')}
                      </span>
                    )}
                  </p>
                </div>
              </div>
              
              {hasRenewalInfo && (
                <div className="flex gap-2 items-start">
                  <RefreshCw className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium">{t('renewalTerms')}</p>
                    <p className="text-sm text-muted-foreground">
                      {t('everyInterval', { 
                        interval: contract.type.renewal_interval,
                        unit: t(`timeUnit.${contract.type.renewal_unit.toLowerCase()}${contract.type.renewal_interval > 1 ? 's' : ''}`)
                      })}
                    </p>
                  </div>
                </div>
              )}
              
              <div className="flex gap-2 items-start">
                {contract.type.direction && getDirectionIcon(contract.type.direction, "h-5 w-5 text-muted-foreground shrink-0 mt-0.5")}
                {!contract.type.direction && <Tag className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />}
                <div>
                  <p className="font-medium">{t('contractType')}</p>
                  <div className="text-sm space-y-1">
                    <p className="text-muted-foreground">{contract.type.name}</p>
                    {contract.type.direction && (
                      <p className="text-xs text-muted-foreground flex items-center">
                        <DirectionText direction={contract.type.direction} />
                      </p>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="flex gap-2 items-start">
                <Tag className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium">{t('category')}</p>
                  <p className="text-sm text-muted-foreground">
                    {contract.category?.name || "No category"}
                  </p>
                </div>
              </div>
              
              <div className="flex gap-2 items-start">
                <Coins className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium">{costLabel}</p>
                  <div className="text-sm text-muted-foreground flex items-center flex-wrap gap-2">
                    <span>
                      {contract.expectedCost.toLocaleString()} {currencyDisplay.symbol && `(${currencyDisplay.symbol})`}
                    </span>
                    
                    {convertedAmount !== null && defaultCurrencyData && (
                      <FeatureGuard feature="currency_conversion">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="flex items-center gap-1 text-xs px-2 py-0.5 bg-blue-100 dark:bg-blue-900 rounded-full cursor-help">
                                <RefreshCw className="h-3 w-3 text-blue-500" />
                                <span className="text-blue-700 dark:text-blue-300">
                                  {convertedAmount.toLocaleString(undefined, {maximumFractionDigits: 2})} 
                                  {defaultCurrencyData.symbol}
                                </span>
                                <AlertTriangle className="h-3 w-3 text-amber-500 ml-0.5 opacity-70" />
                              </div>
                            </TooltipTrigger>
                            <TooltipContent className="bg-black text-white border-gray-800 p-3 max-w-[300px]">
                              <div className="space-y-2">
                                <p>Converted at rate: 1 {currencyDisplay.code} = {conversionRate.toFixed(4)} {defaultCurrencyData.code}</p>
                                <p className="text-amber-300 text-xs flex items-start gap-1.5">
                                  <AlertTriangle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                                  <span>{t('currencyDisclaimer', 'Currency conversions are estimates and may not reflect exact rates at the time of contract payment.')}</span>
                                </p>
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </FeatureGuard>
                    )}
                    
                    {isConverting && (
                      <FeatureGuard feature="currency_conversion">
                        <span className="text-xs text-gray-400">converting...</span>
                      </FeatureGuard>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="flex gap-2 items-start">
                <RefreshCw className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium">{t('paymentFrequency')}</p>
                  <p className="text-sm text-muted-foreground">
                    {formatPaymentFrequency(contract.paymentFrequency, t)}
                  </p>
                </div>
              </div>
            </CardContent>
            <CardFooter>
              <Button variant="outline" size="sm" className="w-full" asChild>
                <Link to={`/contracts/${contract.id}/timeline`}>
                  {getTimelineButtonText()}
                </Link>
              </Button>
            </CardFooter>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <div className="space-y-1">
                <CardTitle>{t('contactPersons')}</CardTitle>
                <CardDescription>{t('peopleAssociated')}</CardDescription>
              </div>
            </CardHeader>
            <CardContent className="pt-4">
              {contract.contactPersons.length === 0 ? (
                <div className="text-center py-4 text-muted-foreground">
                  <p>{t('noContacts')}</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {contract.contactPersons.map((contact) => (
                    <div key={contact.id} className="flex items-start gap-3">
                      <Avatar className="h-10 w-10">
                        <AvatarFallback className="bg-primary/10 text-primary">
                          {contact.name
                            .split(" ")
                            .map((n) => n[0])
                            .join("")}
                        </AvatarFallback>
                      </Avatar>
                      <div className="space-y-1">
                        <div className="flex items-center">
                          <h4 className="font-medium">{contact.name}</h4>
                          <Badge className="ml-2 text-xs" variant="outline">
                            {contact.role}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-3 text-sm text-muted-foreground">
                          <div className="flex items-center gap-1">
                            <span>{contact.email}</span>
                          </div>
                          {contact.phone && (
                            <div className="flex items-center gap-1">
                              <span>{contact.phone}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
            <CardFooter>
              <Button variant="outline" size="sm" className="w-full" asChild>
                <Link to="/contacts">
                  {t('manageContacts')}
                </Link>
              </Button>
            </CardFooter>
          </Card>
        </div>

        <FeatureGuard feature="contract_services">
          {contract.contractServices && contract.contractServices.length > 0 && (
            <ContractServicesCard services={contract.contractServices} />
          )}
        </FeatureGuard>

        <AttachmentsList
          contractId={contract.id}
          attachments={contract.attachments}
        />

        <SendContractEmailDialog
          contract={contract}
          open={emailDialogOpen}
          onOpenChange={setEmailDialogOpen}
        />
      </div>
    </Shell>
  );
}
